import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Snackbar, Alert, IconButton } from '@mui/material'
import Slide from '@mui/material/Slide'
import { AnalyticEvent } from 'src/analytics/adobe/types'
import useDeviceType from 'src/rsmCoreComponents/hooks/useDeviceType'
import { CloseIcon, CircleErrorIcon } from '@rsmus/myrsm-icons'
import tokens from '../../../../styles/tokens.json'

const analyticEventType: AnalyticEvent = 'FailureBannerPage_View'

export type OwnProps = {
  message?: string
  header?: string
  open: boolean
  close: () => void
  testId: string
  disableAutoHide?: boolean
  customMessageSlot?: React.ReactNode
  messageElement?: React.ReactNode
  displayInModal?: boolean
}

const CustomErrorAlert = ({
  header = undefined,
  message = undefined,
  open,
  close,
  testId,
  disableAutoHide = false,
  customMessageSlot = null,
  displayInModal = false,
  messageElement = null,
}: OwnProps) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceType()
  const toastMsgTimelimit = disableAutoHide ? null : 4000
  const toastMsgSlideEnter = 1000
  const toastMsgSlideExit = 1000

  useEffect(() => {
    if (open) {
      ;(window as any).pendo?.track(analyticEventType, {
        testId,
        dataAnalyticsId: testId,
        timestamp: new Date(),
      })
    }
  }, [open])

  const getAlertMessage = () => (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '1rem',
        height: '100%',
        justifyContent: 'center',
        [theme.breakpoints.only('mobile')]: {
          paddingLeft: 0,
        },
      })}>
      <Box
        role="alert"
        sx={(theme) => ({
          fontSize: '1rem',
          textAlign: {
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
          },
          [theme.breakpoints.only('mobile')]: {
            textAlign: 'center',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          },
        })}
        style={{ color: tokens.colors.rsmGray.copy }}>
        {customMessageSlot ? (
          <Box data-testid={`${testId}_CustomErrorContainer`}>
            {customMessageSlot}
          </Box>
        ) : (
          <>
            <Box
              component="span"
              className="title"
              sx={(theme) => ({
                fontFamily: tokens.type.fontFamilies.preloBold,
                fontSize: '1.125rem',
                lineHeight: '1.75rem',
                [theme.breakpoints.only('mobile')]: {
                  fontSize: '1.5rem',
                  paddingTop: '1rem',
                },
              })}
              data-testid={`${testId}_Header`}
              data-analytic-event={analyticEventType}
              data-analytic-id={`${testId}_Header`}>
              {header ? t(header) : ''}
            </Box>
            <Box
              sx={(theme) => ({
                fontFamily: tokens.type.fontFamilies.preloBook,
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                [theme.breakpoints.only('mobile')]: {
                  fontSize: '1rem',
                  paddingBottom: '1rem',
                },
              })}
              data-testid={`${testId}_Message`}>
              {}
              {message ? t(message) : undefined}
              {messageElement || undefined}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )

  const getMobileView = () => (
    <Box
      sx={{
        alignContent: 'center',
        justifyItems: 'center',
        paddingTop: '1.5rem',
      }}>
      <CircleErrorIcon
        size={isMobile ? '7.4375rem' : '4rem'}
        color={tokens.colors.rsmGray.copy}
        fillColor
      />
      {getAlertMessage()}
    </Box>
  )

  return (
    <Box
      sx={
        displayInModal
          ? {
              position: 'inherit',
              height: '6.125rem',
            }
          : {}
      }>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={{
          enter: toastMsgSlideEnter,
          exit: toastMsgSlideExit,
        }}
        TransitionComponent={Slide}
        autoHideDuration={toastMsgTimelimit}
        open={open}
        onClose={() => close()}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          right: displayInModal ? '0rem !important' : '1.5rem !important',
          minHeight: '5.125rem',
          position: displayInModal ? 'absolute' : '',
          top: displayInModal ? '0rem !important' : '',
          left: displayInModal ? '0rem !important' : '',
        }}
        data-testid={`${testId}_Snackbar`}
        data-analytic-event={analyticEventType}
        data-analytic-id={testId}>
        <Alert
          role="presentation"
          aria-atomic="true"
          aria-live="assertive"
          sx={(theme) => ({
            backgroundColor: tokens.colors.warning.secondary,
            fontWeight: 500,
            color: '#515356',
            display: 'flex',
            [theme.breakpoints.only('mobile')]: {
              alignItems: 'center',
              justifyItems: 'center',
            },
            alignItems: 'left',
            justifyContent: 'left',
            position: 'relative',
            width: '100%',
            minHeight: 'Hug(5.125rem)',
            paddingTop: '0.5rem',
            '.MuiAlert-icon': {
              margin: 0,
              paddingLeft: '0.625rem',
            },
            fontFamily: 'Prelo-Book',
            '.MuiAlert-message': {
              [theme.breakpoints.up('tablet')]: {
                paddingRight: '3rem',
              },
              '.subTitle': {
                fontSize: '1.5rem',
              },
              width: '100%',
            },
            '.MuiAlert-action': {
              position: 'absolute',
              top: '0.313rem',
              right: '1.875rem',
            },
          })}
          severity="error"
          onClose={close}
          icon={
            !isMobile && (
              <Box sx={{ alignContent: 'center', paddingLeft: '3.5rem' }}>
                <CircleErrorIcon
                  size="4rem"
                  color={tokens.colors.rsmGray.copy}
                  fillColor
                />
              </Box>
            )
          }
          action={
            <IconButton
              autoFocus
              aria-label={t('Alert.CloseAlert')}
              role="button"
              onClick={close}
              disableFocusRipple
              data-testid={`${testId}_CloseButton`}>
              <CloseIcon size={18} color={tokens.colors.rsmGray.copy} />
            </IconButton>
          }>
          {isMobile && getMobileView()}
          {!isMobile && getAlertMessage()}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CustomErrorAlert
